import type { DataTypeDto, ValueHistoryDto } from '@bk/contracts';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Card } from '../../ui';

const day = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('it-IT');

/**
 * La storia dei campi di una voce (ADR 0248 § 4): per i campi che hanno avuto più valori, il valore di ogni giorno con
 * la fonte e il passo da cui viene (ADR 0251 § 2), dal più recente: dicono com'era (l'uso di un immobile, il saldo).
 */
export function ValueHistory({ type, rif }: { type: DataTypeDto; rif: string }) {
  const { isManager } = useTenant();
  const q = useTenantQuery<ValueHistoryDto>(['model', 'history', type.chiave, rif], `/model/history?${new URLSearchParams({ tipo: type.chiave, rif }).toString()}`, { enabled: isManager });
  const rows = q.data?.storia ?? [];
  const byAttr = new Map<string, ValueHistoryDto['storia']>();
  for (const r of rows) byAttr.set(r.attributo, [...(byAttr.get(r.attributo) ?? []), r]);
  // Solo i campi con almeno due valori diversi: un valore solo non ha storia da mostrare.
  const changed = [...byAttr.entries()].filter(([, list]) => new Set(list.map((x) => JSON.stringify(x.valore))).size > 1);
  if (!changed.length) return null;
  const label = (a: string) => type.attributi.find((x) => x.nome === a)?.etichetta ?? a.replace(/_/g, ' ');
  return (
    <Card>
      <h2 className="text-sm font-semibold">Com’è cambiata nel tempo</h2>
      <p className="mt-1 text-xs text-slate-500">Il valore di ogni giorno con la sua fonte e il passo da cui viene, dal più recente.</p>
      <div className="mt-3 space-y-3">
        {changed.map(([attr, list]) => (
          <div key={attr}>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label(attr)}</h3>
            <ul className="mt-1 divide-y divide-slate-100 text-sm">
              {list.map((x, i) => (
                <li key={`${x.data}|${x.fonte}`} className="flex flex-wrap items-baseline gap-x-3 py-1">
                  <span className="w-24 shrink-0 text-xs text-slate-500">{day(x.data)}</span>
                  <span className={i === 0 ? 'font-medium' : 'text-slate-600'}>{String(x.valore)}</span>
                  <span className="text-xs text-slate-400">{x.fonte}</span>
                  {x.grezzo ? <span className="text-xs text-slate-400">scritto «{x.grezzo}»</span> : null}
                  {x.citazione ? <span className="basis-full pl-[6.75rem] text-xs italic text-slate-500">«{x.citazione}»</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}
